/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import { isContextRoot, knowableAt, canonicalizeContextPath } from './context-roots';

/**
 * **Where an expression can resolve, read from what it reads** (`RT4`, `RESOLUTION_TIER_DESIGN.md`
 * §6a `Q7`, slice `R-d`, `#1535`).
 *
 * It is a property of the expression, and it is **derived, never stored**: the answer combines the
 * expression's inputs with a declaration the expression does not own — what a driver is handed
 * (`knowableAt`, over `DRIVER_SESSION_PATHS` and `DRIVER_POD_FIELDS`) — and that declaration changes
 * (`R-c` changed it, and `round` is new here). A stamp written at save would keep the old answer on
 * every document nobody re-saves. So every reader asks this function: the console's editor, `RT7`'s
 * refusal, and plan construction in opt-engine. One answer, from the declarations in force.
 *
 * The rule needs no list of its own. An expression resolves:
 *
 * | Site | When |
 * | --- | --- |
 * | `driver` | every input is knowable where a driver builds a request (`macroContext`) — today's behaviour, and preferred |
 * | `plan` | otherwise, every input is knowable at plan construction (`planConstruction`): opt-engine resolves it and ships a literal |
 * | `unresolvable` | neither: it mixes an input only opt-engine holds with one only the request has (`Q9`) |
 *
 * **A macro token is a driver input**: `resolveMacro` exists only in the driver. A path whose first
 * segment is no declared root is read against the pod's flattened fields by `resolveMacroPath`, which
 * is supported (`#1000`), and it is not an input either site has a declaration for, so it decides
 * nothing here.
 *
 * Typed structurally rather than as `MacroExpression`, so this file imports only `context-roots` and
 * the console can alias it (`shared-modules.spec.ts`).
 */

/** Where an expression resolves. */
export type ResolutionSiteKind = 'driver' | 'plan' | 'unresolvable';

export interface ExpressionResolutionSite {
  site: ResolutionSiteKind;
  /** Inputs only plan construction can read — what makes the site `plan`. */
  planOnly: string[];
  /** Inputs only the driver can read, when they are what makes the site `unresolvable`. */
  driverOnly: string[];
  /** Why it is `unresolvable`, for the editor to say (`RT7`). Absent otherwise. */
  reason?: string;
}

/** What an expression reads: context paths, and macro tokens by name. */
export interface ExpressionInputs {
  paths: string[];
  macros: string[];
}

/** Looks up a shared expression by id, for a reference. `null` where the caller has no library. */
export type SharedExpressionLookup = ((ref: string) => unknown) | null;

const DRIVER = 'macroContext';
const PLAN = 'planConstruction';

/**
 * Every input an expression reads, at any depth: a calculation's path, a computed operand (a nested
 * expression, a collection's list, a legacy path), both sides of every condition under `all`, `any`
 * and `not`, both branches, and through a reference into the shared expression it names.
 */
export function expressionInputs(expression: unknown, lookup: SharedExpressionLookup): ExpressionInputs {
  const inputs: ExpressionInputs = { paths: [], macros: [] };
  walk(expression, lookup, inputs, new Set());
  return inputs;
}

/** Where this expression resolves (`Q7`). */
export function resolutionSiteOf(expression: unknown, lookup: SharedExpressionLookup): ExpressionResolutionSite {
  const { paths, macros } = expressionInputs(expression, lookup);
  const declared = paths.map(canonicalizeContextPath).filter((p) => isContextRoot(p.split('.')[0]));

  const atDriver = (p: string) => knowableAt(DRIVER, p) === true;
  const atPlan = (p: string) => knowableAt(PLAN, p) === true;

  const planOnly = unique(declared.filter((p) => !atDriver(p) && atPlan(p)));
  if (planOnly.length === 0) return { site: 'driver', planOnly, driverOnly: [] };

  const driverOnly = unique([...declared.filter((p) => atDriver(p) && !atPlan(p)), ...macros.map((m) => `{${m}}`)]);
  if (driverOnly.length === 0) return { site: 'plan', planOnly, driverOnly };

  return {
    site: 'unresolvable',
    planOnly,
    driverOnly,
    reason:
      `This reads ${list(planOnly)}, which only exists while the round is being planned, and ${list(driverOnly)}, ` +
      `which only exists once the request is being built. No step sees both, so it can never resolve. ` +
      `Read only one of the two kinds in this override.`,
  };
}

function walk(node: unknown, lookup: SharedExpressionLookup, into: ExpressionInputs, seen: Set<string>): void {
  if (!node || typeof node !== 'object') return;
  const n = node as Record<string, unknown>;
  switch (n.type) {
    case 'calculated':
    case 'path':
    case 'collection':
      if (typeof n.path === 'string' && n.path.trim() !== '') into.paths.push(n.path.trim());
      return;
    case 'macro':
      if (typeof n.name === 'string') into.macros.push(n.name);
      return;
    case 'computed':
      for (const operand of Array.isArray(n.operands) ? n.operands : []) walk(operand, lookup, into, seen);
      return;
    case 'conditional':
      walkCriteria(n.condition, lookup, into, seen);
      walk(n.then, lookup, into, seen);
      walk(n.else, lookup, into, seen);
      return;
    case 'reference': {
      const ref = typeof n.ref === 'string' ? n.ref : undefined;
      if (!ref || !lookup || seen.has(ref)) return;
      seen.add(ref);
      walk(lookup(ref), lookup, into, seen);
      return;
    }
    default:
      // `static`, `suppress`, `bound`, a `literal` or `fn` operand: no input.
      return;
  }
}

/** A criteria tree, or a bare condition as a pre-Criteria document stores one (`toCriteriaNode`). */
function walkCriteria(node: unknown, lookup: SharedExpressionLookup, into: ExpressionInputs, seen: Set<string>): void {
  if (!node || typeof node !== 'object') return;
  const n = node as Record<string, unknown>;
  if (n.type === 'all' || n.type === 'any') {
    for (const child of Array.isArray(n.of) ? n.of : []) walkCriteria(child, lookup, into, seen);
    return;
  }
  if (n.type === 'not') return walkCriteria(n.of, lookup, into, seen);
  const condition = (n.type === 'condition' ? n.condition : n) as Record<string, unknown> | undefined;
  if (!condition || typeof condition !== 'object') return;
  // Both spellings of each side, as `readConditionOperands` reads them: an operand, or a legacy path.
  if (condition.left) walk(condition.left, lookup, into, seen);
  else if (typeof condition.path === 'string') into.paths.push(condition.path);
  if (condition.right) walk(condition.right, lookup, into, seen);
  else if (typeof condition.valuePath === 'string') into.paths.push(condition.valuePath);
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function list(values: string[]): string {
  const quoted = values.map((v) => `\`${v}\``);
  return quoted.length <= 1 ? quoted.join('') : `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
}
